import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { SegmentCondition } from '@/pages/SegmentRuleFields'
import type { ChangeEvent } from 'react'

export function CompletedCourseConditionRow({
  condition,
  courses,
  onChange,
  onRemove,
}: ConditionRowProps & { courses: Array<{ id: number; title: string; status?: string }> }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-input px-3 py-2">
      <span className="text-body-sm">Completed Course</span>
      <Select
        value={String(condition.values[0] ?? '')}
        onValueChange={(value) =>
          onChange({ ...condition, values: [Number(value), condition.values[1] ?? ''] })
        }
      >
        <SelectTrigger aria-label="Completed Course" className="h-8 w-48">
          <SelectValue placeholder="Choose a Course" />
        </SelectTrigger>
        <SelectContent>
          {courses.map((course) => (
            <SelectItem key={course.id} value={String(course.id)}>
              {course.title}
              {course.status === 'archived' ? ' (archived)' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-body-sm text-muted-foreground">on or after</span>
      <Input
        type="date"
        aria-label="Completed Course since date"
        className="h-8 w-36"
        value={String(condition.values[1] ?? '')}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onChange({ ...condition, values: [condition.values[0], event.target.value] })
        }
      />
      <RemoveButton onClick={onRemove} />
    </div>
  )
}

export const membershipStatuses = [
  ['active', 'Active'],
  ['lapsed', 'Lapsed'],
  ['suspended', 'Suspended'],
  ['pending_new', 'Pending-New'],
  ['pending_renewal', 'Pending-Renewal'],
] as const

type MembershipStatus = (typeof membershipStatuses)[number][0]

interface ConditionRowProps {
  condition: SegmentCondition
  onChange: (condition: SegmentCondition) => void
  onRemove: () => void
}

export function MembershipStatusCondition({
  conditions,
  update,
}: {
  conditions: SegmentCondition[]
  update: (field: 'membership_status', next?: SegmentCondition) => void
}) {
  const statuses = (conditions.find((condition) => condition.field === 'membership_status')
    ?.values ?? []) as MembershipStatus[]
  return (
    <div className="flex items-start gap-2 py-1.5">
      <span className="w-9 shrink-0 pt-1 text-right text-caption uppercase tracking-wide text-muted-foreground">
        if
      </span>
      <div className="flex min-h-8 flex-1 flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-input px-3 py-1.5">
        <span className="text-body-sm text-foreground">Membership status is any of</span>
        {membershipStatuses.map(([value, label]) => (
          <Label key={value} className="flex items-center gap-1.5 text-body-sm font-normal">
            <Checkbox
              checked={statuses.includes(value)}
              aria-label={label}
              onCheckedChange={(checked) => {
                const next = checked
                  ? [...statuses, value]
                  : statuses.filter((status) => status !== value)
                update(
                  'membership_status',
                  next.length
                    ? { field: 'membership_status', operator: 'any_of', values: next }
                    : undefined,
                )
              }}
            />
            {label}
          </Label>
        ))}
      </div>
    </div>
  )
}

export function AnyOfConditionRow({
  label,
  condition,
  options,
  allowInverse = false,
  onChange,
  onRemove,
}: ConditionRowProps & {
  label: string
  options: Array<{ value: string | number; label: string }>
  allowInverse?: boolean
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-input px-3 py-2">
      {allowInverse ? (
        <>
          <span className="text-body-sm">{label}</span>
          <Select
            value={condition.operator}
            onValueChange={(operator) => onChange({ ...condition, operator })}
          >
            <SelectTrigger aria-label={`${label} comparison`} className="h-8 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any_of">is any of</SelectItem>
              <SelectItem value="none_of">is not any of</SelectItem>
            </SelectContent>
          </Select>
        </>
      ) : (
        <span className="text-body-sm">
          {`${label} ${condition.operator === 'none_of' ? 'is not any of' : 'is any of'}`}
        </span>
      )}
      {options.map((option) => (
        <Label key={option.value} className="flex items-center gap-1.5 text-body-sm font-normal">
          <Checkbox
            checked={condition.values.includes(option.value)}
            aria-label={`${label} ${option.label}`}
            onCheckedChange={(checked) =>
              changeTicks(condition, option.value, checked === true, onChange, onRemove)
            }
          />
          {option.label}
        </Label>
      ))}
      <RemoveButton onClick={onRemove} />
    </div>
  )
}

/**
 * Tick or untick one box of an any-of row. Clearing the last box removes the
 * condition, the same as the Remove button: an any-of condition with no values
 * is one the API refuses, so the editor never keeps such a row.
 */
function changeTicks(
  condition: SegmentCondition,
  value: string | number,
  checked: boolean,
  onChange: (condition: SegmentCondition) => void,
  onRemove: () => void,
) {
  const values = checked
    ? [...condition.values, value]
    : condition.values.filter((current) => current !== value)
  if (values.length === 0) onRemove()
  else onChange({ ...condition, values })
}

export function ChapterConditionRow({
  condition,
  chapters,
  onChange,
  onRemove,
}: ConditionRowProps & {
  chapters: Array<{ id: number; name: string; archived?: boolean }>
}) {
  const mode = condition.operator === 'is' ? String(condition.values[0]) : 'specific'
  const availableChapters = chapters.filter(
    (chapter) => !chapter.archived || condition.values.includes(chapter.id),
  )
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-input px-3 py-2">
      <span className="text-body-sm">Chapter</span>
      <Select
        value={mode}
        onValueChange={(value) =>
          onChange(
            value === 'specific'
              ? { field: 'chapter_membership', operator: 'any_of', values: [] }
              : { field: 'chapter_membership', operator: 'is', values: [value] },
          )
        }
      >
        <SelectTrigger aria-label="Chapter criterion" className="h-8 w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="specific">in a specific chapter</SelectItem>
          <SelectItem value="any">in any chapter</SelectItem>
          <SelectItem value="none">in no chapter</SelectItem>
        </SelectContent>
      </Select>
      {mode === 'specific' &&
        availableChapters.map((chapter) => (
          <Label key={chapter.id} className="flex items-center gap-1.5 text-body-sm font-normal">
            <Checkbox
              checked={condition.values.includes(chapter.id)}
              aria-label={`Chapter ${chapter.name}${chapter.archived ? ' (archived)' : ''}`}
              onCheckedChange={(checked) =>
                changeTicks(
                  { ...condition, operator: 'any_of' },
                  chapter.id,
                  checked === true,
                  onChange,
                  onRemove,
                )
              }
            />
            {chapter.name}
            {chapter.archived ? ' (archived)' : ''}
          </Label>
        ))}
      <RemoveButton onClick={onRemove} />
    </div>
  )
}

export function BooleanConditionRow({
  field,
  condition,
  onChange,
  onRemove,
}: ConditionRowProps & { field: 'autopay' | 'archived' }) {
  const autopay = field === 'autopay'
  const label = autopay ? 'Autopay enrollment' : 'Archived'
  const options = autopay
    ? [
        ['on', 'on'],
        ['off', 'off'],
      ]
    : [
        ['yes', 'yes'],
        ['no', 'no'],
      ]
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-input px-3 py-2">
      <span className="text-body-sm">{label} is</span>
      <Select
        value={String(condition.values[0])}
        onValueChange={(value) => onChange({ ...condition, values: [value] })}
      >
        <SelectTrigger aria-label={label} className="h-8 w-32">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(([value, text]) => (
            <SelectItem key={value} value={value}>
              {text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <RemoveButton onClick={onRemove} />
    </div>
  )
}

function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <Button type="button" variant="outline" onClick={onClick}>
      Remove
    </Button>
  )
}
